"use client"

import * as React from "react"
import { Dialog } from "@base-ui/react/dialog"
import { XIcon } from "lucide-react"

import { cn } from "@/lib/utils"

function Sheet({
  open,
  onOpenChange,
  defaultOpen,
  children,
  ...props
}: {
  open?: boolean
  onOpenChange?: (open: boolean, eventDetails: any) => void
  defaultOpen?: boolean
  children?: React.ReactNode
  modal?: boolean
}) {
  return (
    <Dialog.Root
      data-slot="sheet"
      open={open}
      onOpenChange={onOpenChange}
      defaultOpen={defaultOpen}
      {...props}
    >
      {children}
    </Dialog.Root>
  )
}

function SheetTrigger({
  asChild,
  children,
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { asChild?: boolean }) {
  if (asChild && React.isValidElement(children)) {
    return (
      <Dialog.Trigger
        data-slot="sheet-trigger"
        className={className}
        render={children as React.ReactElement}
        {...(props as any)}
      />
    )
  }
  return (
    <Dialog.Trigger
      data-slot="sheet-trigger"
      className={className}
      {...(props as any)}
    >
      {children}
    </Dialog.Trigger>
  )
}

function SheetClose({
  asChild,
  children,
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { asChild?: boolean }) {
  if (asChild && React.isValidElement(children)) {
    return (
      <Dialog.Close
        data-slot="sheet-close"
        className={className}
        render={children as React.ReactElement}
        {...(props as any)}
      />
    )
  }
  return (
    <Dialog.Close
      data-slot="sheet-close"
      className={className}
      {...(props as any)}
    >
      {children}
    </Dialog.Close>
  )
}

function SheetPortal({ children }: { children?: React.ReactNode }) {
  return <Dialog.Portal>{children}</Dialog.Portal>
}

function SheetOverlay({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <Dialog.Backdrop
      data-slot="sheet-overlay"
      className={cn(
        "fixed inset-0 z-50 bg-black/50 transition-all duration-300",
        "data-[ending-style]:opacity-0 data-[starting-style]:opacity-0",
        className
      )}
      {...(props as any)}
    />
  )
}

const SheetContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    side?: "top" | "right" | "bottom" | "left"
    showCloseButton?: boolean
  }
>(({
  className,
  children,
  side = "right",
  showCloseButton = true,
  ...props
}, ref) => {
  return (
    <SheetPortal>
      <SheetOverlay />
      <Dialog.Popup
        ref={ref}
        data-slot="sheet-content"
        data-side={side}
        className={cn(
          "bg-background fixed z-50 flex flex-col gap-4 shadow-lg transition-transform duration-500 ease-in-out",
          "data-[ending-style]:duration-300",
          side === "right" && [
            "inset-y-0 right-0 h-full w-3/4 border-l sm:max-w-sm",
            "data-[starting-style]:translate-x-full",
            "data-[ending-style]:translate-x-full",
          ],
          side === "left" && [
            "inset-y-0 left-0 h-full w-3/4 border-r sm:max-w-sm",
            "data-[starting-style]:-translate-x-full",
            "data-[ending-style]:-translate-x-full",
          ],
          side === "top" && [
            "inset-x-0 top-0 h-auto border-b",
            "data-[starting-style]:-translate-y-full",
            "data-[ending-style]:-translate-y-full",
          ],
          side === "bottom" && [
            "inset-x-0 bottom-0 h-auto border-t",
            "data-[starting-style]:translate-y-full",
            "data-[ending-style]:translate-y-full",
          ],
          className
        )}
        {...(props as any)}
      >
        {children}
        {showCloseButton && (
          <Dialog.Close className="ring-offset-background focus:ring-ring data-[state=open]:bg-secondary absolute top-8 right-4 rounded-sm opacity-100 transition-opacity hover:opacity-80 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none bg-muted hover:bg-muted/80 p-1.5">
            <XIcon className="size-4" />
            <span className="sr-only">Close</span>
          </Dialog.Close>
        )}
      </Dialog.Popup>
    </SheetPortal>
  )
})
SheetContent.displayName = "SheetContent"

function SheetHeader({ className, ...props }: React.ComponentProps<"div">) {
  // pr-12 reserves space for the absolutely-positioned close button
  // rendered by SheetContent (top-8 right-4 size-4 + padding). Without
  // it, a long title (e.g. "Commission Review") was getting overlapped
  // by the X icon on narrow viewports.
  return (
    <div
      data-slot="sheet-header"
      className={cn("flex flex-col gap-1.5 p-4 pr-12", className)}
      {...props}
    />
  )
}

function SheetFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-footer"
      className={cn("mt-auto flex flex-col gap-2 p-4", className)}
      {...props}
    />
  )
}

function SheetTitle({
  className,
  ...props
}: React.HTMLAttributes<HTMLHeadingElement>) {
  return (
    <Dialog.Title
      data-slot="sheet-title"
      className={cn("text-foreground font-semibold", className)}
      {...(props as any)}
    />
  )
}

function SheetDescription({
  className,
  ...props
}: React.HTMLAttributes<HTMLParagraphElement>) {
  return (
    <Dialog.Description
      data-slot="sheet-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...(props as any)}
    />
  )
}

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
}
